import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createBook } from '../../api/books';
import api from '../../api/client';
import { useQuery as useQ } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import FormField, { Input, Select, Textarea, FormRow, SubmitBtn } from '../ui/FormField';
import toast from 'react-hot-toast';
import { Search, Loader, CheckCircle } from 'lucide-react';

export default function AddBookModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title:'', author:'', isbn:'', collection_id:'',
    publisher:'', pub_year:'', language:'ka', description:'', copies:'1',
    account_code:'', sub_account:'', full_account:'', acquisition_price:'', editor:'', translator:'', place_of_publication:'', material_type:'book', subject:'', series:'', series_number:'', volume:'', volume_number:'', udc:'', shelf_code:'', inventory_number:'', notes:'', location:''
  });
  const [loading, setLoading] = useState(false);
  const [isbnLoading, setIsbnLoading] = useState(false);
  const [isbnFound, setIsbnFound] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');

  const { data: accounts } = useQ({
    queryKey: ['accounts'],
    queryFn: () => api.get('/acquisitions/accounts'),
    enabled: open
  } as any);

  const { data: colls } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get('/books/collections'),
    enabled: open
  } as any);

  const set = (k: string) => (e: any) => {
    setForm(f => ({...f, [k]: e.target.value}));
    if (k === 'isbn') setIsbnFound(false);
  };

  const lookupISBN = async () => {
    if (!form.isbn || form.isbn.length < 10) return toast.error('ISBN სწორად შეიყვანე');
    setIsbnLoading(true);
    setIsbnFound(false);
    try {
      const { data } = await api.get(`/books/isbn/${form.isbn.replace(/[-\s]/g, '')}`);
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        author: data.author || f.author,
        publisher: data.publisher || f.publisher,
        pub_year: data.pub_year || f.pub_year,
        description: data.description || f.description,
        language: data.language === 'geo' ? 'ka' : data.language || f.language,
      }));
      if (data.cover_url) setCoverUrl(data.cover_url);
      setIsbnFound(true);
      toast.success(`მოიძებნა: ${data.title} (${data.source})`);
    } catch {
      toast.error('ISBN ვერ მოიძებნა — ხელით შეიყვანე');
    } finally { setIsbnLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) return toast.error('სათაური სავალდებულოა');
    setLoading(true);
    try {
      await createBook({...form, cover_url: coverUrl});
      toast.success('წიგნი დაემატა!');
      qc.invalidateQueries({ queryKey: ['books'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      setForm({ title:'', author:'', isbn:'', collection_id:'', publisher:'', pub_year:'', language:'ka', description:'', copies:'1', account_code:'', sub_account:'', full_account:'', acquisition_price:'', editor:'', translator:'', place_of_publication:'', material_type:'book', subject:'', series:'', series_number:'', volume:'', volume_number:'', udc:'', shelf_code:'', inventory_number:'', notes:'', location:'' });
      setCoverUrl('');
      setIsbnFound(false);
      onClose();
    } catch { toast.error('შეცდომა'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="წიგნის დამატება" width={540}>
      <form onSubmit={handleSubmit}>

        <div style={{marginBottom:16,padding:14,background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:10}}>
          <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.05em'}}>ISBN-ით ავტომატური ძიება</div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1,position:'relative'}}>
              <Input
                value={form.isbn}
                onChange={set('isbn')}
                placeholder="978-..."
                onKeyDown={e => { if(e.key==='Enter') { e.preventDefault(); lookupISBN(); } }}
              />
            </div>
            <button type="button" onClick={lookupISBN} disabled={isbnLoading}
              style={{display:'flex',alignItems:'center',gap:5,padding:'9px 14px',background:isbnFound?'var(--success-bg)':'#1d4ed8',border:`1px solid ${isbnFound?'#0a4a2a':'transparent'}`,borderRadius:8,color:isbnFound?'var(--success)':'white',fontSize:12,fontWeight:500,cursor:'pointer',flexShrink:0,opacity:isbnLoading?0.7:1}}>
              {isbnLoading ? <Loader size={13} style={{animation:'spin 1s linear infinite'}} /> : isbnFound ? <CheckCircle size={13} /> : <Search size={13} />}
              {isbnLoading ? 'ძიება...' : isbnFound ? 'მოიძებნა' : 'ძიება'}
            </button>
          </div>
          {isbnFound && coverUrl && (
            <div style={{marginTop:10,display:'flex',alignItems:'center',gap:10}}>
              <img src={coverUrl} alt="cover" style={{width:40,height:56,objectFit:'cover',borderRadius:4,border:'1px solid #1e2d45'}} />
              <span style={{fontSize:11,color:'var(--success)'}}>გარეკანი ნაპოვნია ✓</span>
            </div>
          )}
        </div>

        <FormField label="სათაური" required>
          <Input value={form.title} onChange={set('title')} placeholder="წიგნის სათაური" />
        </FormField>
        <FormField label="ავტორი">
          <Input value={form.author} onChange={set('author')} placeholder="ავტორი" />
        </FormField>
        <FormRow>
          <FormField label="რედაქტორი">
            <Input value={form.editor} onChange={set('editor')} placeholder="რედაქტორი" />
          </FormField>
          <FormField label="მთარგმნელი">
            <Input value={form.translator} onChange={set('translator')} placeholder="მთარგმნელი" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="მასალის ტიპი">
            <Select value={form.material_type} onChange={set('material_type')}>
              <option value="book">წიგნი</option>
              <option value="periodical">პერიოდიკა</option>
              <option value="dissertation">დისერტაცია</option>
              <option value="dissertation_abstract">სადისერტაციო მაცნე</option>
              <option value="cd">CD</option>
              <option value="proceedings">შრომები</option>
              <option value="xerox">ქსეროასლი</option>
            </Select>
          </FormField>
          <FormField label="ენა">
            <Select value={form.language} onChange={set('language')}>
              <option value="ka">ქართული</option>
              <option value="en">English</option>
              <option value="ru">Русский</option>
              <option value="other">სხვა</option>
            </Select>
          </FormField>
        </FormRow>
        <FormField label="კოლექცია">
          <Select value={form.collection_id} onChange={set('collection_id')}>
            <option value="">— კოლექცია —</option>
            {((colls as any)?.data?.collections||[]).map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </FormField>
        <FormRow>
          <FormField label="გამომცემელი">
            <Input value={form.publisher} onChange={set('publisher')} placeholder="გამომცემლობა" />
          </FormField>
          <FormField label="გამოცემის ადგილი">
            <Input value={form.place_of_publication} onChange={set('place_of_publication')} placeholder="თბილისი..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="გამოცემის წელი">
            <Input value={form.pub_year} onChange={set('pub_year')} placeholder="2024" />
          </FormField>
          <FormField label="ასლების რაოდენობა" required>
            <Input type="number" min="1" max="999" value={form.copies} onChange={set('copies')} placeholder="1" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="სრული საბუღ. ანგარიში *" required>
            <Input value={form.full_account} onChange={set('full_account')} placeholder="2157 1 05922" />
          </FormField>
          <FormField label="ქვეანგარიში">
            <Input value={form.sub_account} onChange={set('sub_account')} placeholder="1" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="საბუღ. კოდი">
            <Input value={form.account_code} onChange={set('account_code')} placeholder="05922" />
          </FormField>
          <FormField label="ასლის ფასი (₾)">
            <Input type="number" min="0" step="0.01" value={form.acquisition_price} onChange={set('acquisition_price')} placeholder="0.00" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="მდებარეობა (დარბაზი)">
            <Input value={form.location} onChange={set('location')} placeholder="დარბაზი A, თარო 3..." />
          </FormField>
          <FormField label="UDC კლასიფიკაცია">
            <Input value={form.udc} onChange={set('udc')} placeholder="94(479.22)..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="საგანი/თემა">
            <Input value={form.subject} onChange={set('subject')} placeholder="ისტორია, ფიზიკა..." />
          </FormField>
          <FormField label="სერია / ტომი">
            <Input value={form.series} onChange={set('series')} placeholder="სერიის სახელი" />
          </FormField>
        </FormRow>
        <FormField label="აღწერა">
          <Textarea value={form.description} onChange={set('description')} placeholder="მოკლე აღწერა..." />
        </FormField>
        <FormField label="შენიშვნა">
          <Input value={form.notes} onChange={set('notes')} placeholder="შიდა შენიშვნა..." />
        </FormField>
        <SubmitBtn loading={loading} label="დამატება" />
      </form>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </Modal>
  );
}
